import { useState, useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { PageTransition, FadeIn, StaggerContainer, StaggerItem } from '../components/layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { ChevronRight, Search, RefreshCw, Clock, User, Mail, Phone, Eye, ArrowLeft, Loader2, MapPin, Gift, Palette, Package } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { toAbsoluteUrl } from '../lib/utils';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const statusOptions = [
  { value: 'pending', label: 'En attente', color: 'bg-yellow-100 text-yellow-800' },
  { value: 'pending_payment', label: 'En attente paiement', color: 'bg-yellow-100 text-yellow-800' },
  { value: 'paid', label: 'Payée', color: 'bg-green-100 text-green-800' },
  { value: 'failed', label: 'Paiement échoué', color: 'bg-red-100 text-red-800' },
  { value: 'expired', label: 'Session expirée', color: 'bg-orange-100 text-orange-800' },
  { value: 'confirmed', label: 'Confirmée', color: 'bg-blue-100 text-blue-800' },
  { value: 'in_progress', label: 'En cours', color: 'bg-purple-100 text-purple-800' },
  { value: 'shipped', label: 'Expédiée', color: 'bg-indigo-100 text-indigo-800' },
  { value: 'completed', label: 'Terminée', color: 'bg-green-100 text-green-800' },
  { value: 'cancelled', label: 'Annulée', color: 'bg-red-100 text-red-800' }
];

const legacyStatusOptions = [
  { value: 'paid_mock', label: 'Payée (historique test)', color: 'bg-blue-100 text-blue-800' }
];

const allStatusOptions = [...statusOptions, ...legacyStatusOptions];

const orderTypeLabels = {
  press_on_nails: { label: 'Press On Nails', icon: Palette, color: 'text-pink-600' },
  formation_product: { label: 'Produit formation', icon: Package, color: 'text-[var(--gold)]' },
  gift_card: { label: 'Carte Cadeau', icon: Gift, color: 'text-rose-600' },
  mixed: { label: 'Mixte', icon: Package, color: 'text-purple-600' }
};

const shapeLabels = {
  square: 'Carré', round: 'Rond', almond: 'Amande',
  stiletto: 'Stiletto', coffin: 'Cercueil', oval: 'Ovale'
};

const lengthLabels = {
  short: 'Court', medium: 'Moyen', long: 'Long', extra_long: 'Extra Long'
};

const getDisplayablePhotoUrl = (photo) => {
  const normalizedUrl = toAbsoluteUrl(photo?.url || photo?.preview || '');
  if (!normalizedUrl || normalizedUrl.startsWith('blob:')) return null;
  return normalizedUrl;
};

export default function AdminOrdersPage() {
  const [orders, setOrders] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);

  useEffect(() => {
    fetchOrders();
  }, [statusFilter, page]);

  const fetchOrders = async () => {
    setIsLoading(true);
    try {
      const params = new URLSearchParams();
      if (statusFilter !== 'all') params.append('status', statusFilter);
      if (searchTerm) params.append('search', searchTerm);
      params.append('page', page.toString());
      params.append('per_page', '20');

      const response = await axios.get(`${API_URL}/admin/orders?${params}`);
      setOrders(response.data.orders);
      setTotal(response.data.total);
    } catch (err) {
      console.error('Error fetching orders:', err);
      toast.error('Erreur lors du chargement des commandes');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(1);
    fetchOrders();
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('fr-FR', {
      day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit'
    });
  };

  const getStatusColor = (status) => {
    return allStatusOptions.find(s => s.value === status)?.color || 'bg-gray-100 text-gray-800';
  };

  const getStatusLabel = (status) => {
    return allStatusOptions.find(s => s.value === status)?.label || status;
  };

  return (
    <PageTransition>
      {/* Hero */}
      <section className="pt-32 pb-8 px-6 bg-[var(--charcoal)]">
        <div className="max-w-[800px] mx-auto text-center">
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="font-['Cormorant_Garamond'] text-4xl md:text-5xl font-light mb-4 text-white"
          >
            Gestion des Commandes
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="text-white/60"
          >
            {total} commande{total > 1 ? 's' : ''} au total
          </motion.p>
        </div>
      </section>

      {/* Breadcrumb */}
      <div className="bg-white py-4 border-b border-[var(--champagne)]">
        <div className="max-w-[1400px] mx-auto px-6">
          <nav className="flex items-center gap-2 text-sm text-[var(--charcoal)]/60">
            <Link to="/" className="hover:text-[var(--gold)]">Accueil</Link>
            <ChevronRight size={14} />
            <Link to="/admin" className="hover:text-[var(--gold)]">Admin</Link>
            <ChevronRight size={14} />
            <span className="text-[var(--charcoal)]">Commandes</span>
          </nav>
        </div>
      </div>

      {/* Content */}
      <section className="py-12 px-6" data-testid="admin-orders-content">
        <div className="max-w-[1200px] mx-auto">
          {/* Filters */}
          <div className="flex flex-col md:flex-row gap-4 mb-8">
            <form onSubmit={handleSearch} className="relative flex-1">
              <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--charcoal)]/40" />
              <Input
                placeholder="Rechercher par ID, email ou nom..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 rounded-none"
                data-testid="search-orders"
              />
            </form>
            <Select value={statusFilter} onValueChange={(v) => { setStatusFilter(v); setPage(1); }}>
              <SelectTrigger className="w-full md:w-[200px] rounded-none" data-testid="filter-status">
                <SelectValue placeholder="Filtrer par statut" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous les statuts</SelectItem>
                {allStatusOptions.map(status => (
                  <SelectItem key={status.value} value={status.value}>{status.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button variant="outline" onClick={fetchOrders} className="rounded-none" data-testid="refresh-orders">
              <RefreshCw size={16} className="mr-2" />
              Actualiser
            </Button>
          </div>

          {/* Orders List */}
          {isLoading ? (
            <div className="text-center py-12">
              <Loader2 size={32} className="animate-spin mx-auto mb-4 text-[var(--gold)]" />
              <p className="text-sm text-[var(--charcoal)]/60">Chargement...</p>
            </div>
          ) : orders.length === 0 ? (
            <div className="text-center py-12 bg-white border border-[var(--champagne)]">
              <p className="text-[var(--charcoal)]/60">Aucune commande trouvée</p>
            </div>
          ) : (
            <>
              <div className="bg-white border border-[var(--champagne)] overflow-hidden">
                {/* Table Header */}
                <div className="hidden md:grid grid-cols-12 gap-4 px-6 py-4 bg-[var(--champagne)]/30 text-xs uppercase tracking-wider text-[var(--charcoal)]/60 font-medium">
                  <div className="col-span-2">Commande</div>
                  <div className="col-span-3">Client</div>
                  <div className="col-span-3">Détails</div>
                  <div className="col-span-2">Date</div>
                  <div className="col-span-2">Statut</div>
                </div>

                <StaggerContainer>
                  {orders.map((order, index) => (
                    <StaggerItem key={order.id}>
                      <Link
                        to={`/admin/commandes/${order.id}`}
                        className="grid grid-cols-1 md:grid-cols-12 gap-4 px-6 py-4 border-b border-[var(--champagne)] last:border-b-0 hover:bg-[var(--champagne)]/10 transition-colors"
                        data-testid={`order-row-${index}`}
                      >
                        <div className="md:col-span-2">
                          <span className="font-mono text-sm">#{order.id.slice(0, 8)}</span>
                        </div>
                        <div className="md:col-span-3">
                          <p className="text-sm font-medium text-[var(--charcoal)]">{order.user_name || 'N/A'}</p>
                          <p className="text-xs text-[var(--charcoal)]/60">{order.user_email}</p>
                        </div>
                        <div className="md:col-span-3">
                          {(() => {
                            const typeInfo = orderTypeLabels[order.order_type] || orderTypeLabels.press_on_nails;
                            const TypeIcon = typeInfo.icon;
                            return (
                              <div className="flex items-center gap-2">
                                <TypeIcon size={14} className={typeInfo.color} />
                                <span className="text-sm">{typeInfo.label}</span>
                              </div>
                            );
                          })()}
                          {order.total_price && (
                            <p className="text-xs text-[var(--charcoal)]/60 mt-1">
                              {order.total_price.toFixed(2)}€
                            </p>
                          )}
                        </div>
                        <div className="md:col-span-2 flex items-center gap-2 text-sm text-[var(--charcoal)]/60">
                          <Clock size={14} />
                          {formatDate(order.created_at)}
                        </div>
                        <div className="md:col-span-2 flex items-center gap-2">
                          <span className={`px-2 py-1 text-xs rounded ${getStatusColor(order.status)}`}>
                            {getStatusLabel(order.status)}
                          </span>
                          <Eye size={16} className="text-[var(--charcoal)]/40 ml-auto" />
                        </div>
                      </Link>
                    </StaggerItem>
                  ))}
                </StaggerContainer>
              </div>

              {/* Pagination */}
              {total > 20 && (
                <div className="flex justify-center gap-2 mt-6">
                  <Button
                    variant="outline"
                    disabled={page === 1}
                    onClick={() => setPage(p => p - 1)}
                    className="rounded-none"
                  >
                    Précédent
                  </Button>
                  <span className="px-4 py-2 text-sm text-[var(--charcoal)]/60">
                    Page {page} / {Math.ceil(total / 20)}
                  </span>
                  <Button
                    variant="outline"
                    disabled={page >= Math.ceil(total / 20)}
                    onClick={() => setPage(p => p + 1)}
                    className="rounded-none"
                  >
                    Suivant
                  </Button>
                </div>
              )}
            </>
          )}
        </div>
      </section>
    </PageTransition>
  );
}

// Order Detail Page Component
export function AdminOrderDetailPage() {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isUpdating, setIsUpdating] = useState(false);
  const [adminNotes, setAdminNotes] = useState('');

  useEffect(() => {
    fetchOrder();
  }, [orderId]);

  const fetchOrder = async () => {
    setIsLoading(true);
    try {
      const response = await axios.get(`${API_URL}/admin/orders/${orderId}`);
      setOrder(response.data);
      setAdminNotes(response.data.admin_notes || '');
    } catch (err) {
      console.error('Error fetching order:', err);
      toast.error('Commande non trouvée');
      navigate('/admin/commandes');
    } finally {
      setIsLoading(false);
    }
  };

  const updateStatus = async (newStatus) => {
    setIsUpdating(true);
    try {
      const response = await axios.patch(`${API_URL}/admin/orders/${orderId}/status`, {
        status: newStatus,
        admin_notes: adminNotes
      });
      setOrder(response.data);
      toast.success('Statut mis à jour');
    } catch (err) {
      console.error('Error updating status:', err);
      toast.error('Erreur lors de la mise à jour');
    } finally {
      setIsUpdating(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('fr-FR', {
      day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit'
    });
  };

  const getStatusColor = (status) => {
    return statusOptions.find(s => s.value === status)?.color || 'bg-gray-100 text-gray-800';
  };

  if (isLoading) {
    return (
      <PageTransition>
        <div className="min-h-screen flex items-center justify-center">
          <Loader2 size={32} className="animate-spin text-[var(--gold)]" />
        </div>
      </PageTransition>
    );
  }

  if (!order) return null;

  return (
    <PageTransition>
      {/* Hero */}
      <section className="pt-32 pb-8 px-6 bg-[var(--charcoal)]">
        <div className="max-w-[800px] mx-auto text-center">
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="font-['Cormorant_Garamond'] text-4xl font-light mb-2 text-white"
          >
            Commande #{order.id.slice(0, 8)}
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="text-white/60"
          >
            {formatDate(order.created_at)}
          </motion.p>
        </div>
      </section>

      {/* Breadcrumb */}
      <div className="bg-white py-4 border-b border-[var(--champagne)]">
        <div className="max-w-[1400px] mx-auto px-6">
          <nav className="flex items-center gap-2 text-sm text-[var(--charcoal)]/60">
            <Link to="/admin" className="hover:text-[var(--gold)]">Admin</Link>
            <ChevronRight size={14} />
            <Link to="/admin/commandes" className="hover:text-[var(--gold)]">Commandes</Link>
            <ChevronRight size={14} />
            <span className="text-[var(--charcoal)]">#{order.id.slice(0, 8)}</span>
          </nav>
        </div>
      </div>

      {/* Content */}
      <section className="py-12 px-6">
        <div className="max-w-[1200px] mx-auto">
          <Button
            variant="ghost"
            onClick={() => navigate('/admin/commandes')}
            className="mb-6"
          >
            <ArrowLeft size={16} className="mr-2" />
            Retour aux commandes
          </Button>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Main Info */}
            <div className="lg:col-span-2 space-y-6">
              {/* Client Info */}
              <FadeIn>
                <div className="bg-white p-6 border border-[var(--champagne)]">
                  <h3 className="font-['Cormorant_Garamond'] text-xl mb-4 text-[var(--charcoal)]">
                    Informations client
                  </h3>
                  {order.customer_info ? (
                    <div className="space-y-3">
                      <div className="flex items-center gap-3">
                        <User size={16} className="text-[var(--gold)]" />
                        <span className="font-medium">
                          {order.customer_info.first_name} {order.customer_info.last_name}
                        </span>
                      </div>
                      <div className="flex items-center gap-3">
                        <Mail size={16} className="text-[var(--gold)]" />
                        <a href={`mailto:${order.customer_info.email}`} className="text-[var(--dark-rosegold)] hover:underline">
                          {order.customer_info.email}
                        </a>
                      </div>
                      {order.customer_info.phone && (
                        <div className="flex items-center gap-3">
                          <Phone size={16} className="text-[var(--gold)]" />
                          <a href={`tel:${order.customer_info.phone}`} className="hover:underline">
                            {order.customer_info.phone}
                          </a>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="space-y-3">
                      <div className="flex items-center gap-3">
                        <User size={16} className="text-[var(--gold)]" />
                        <span>{order.user_name || 'Non renseigné'}</span>
                      </div>
                      <div className="flex items-center gap-3">
                        <Mail size={16} className="text-[var(--gold)]" />
                        <a href={`mailto:${order.user_email}`} className="text-[var(--dark-rosegold)] hover:underline">
                          {order.user_email}
                        </a>
                      </div>
                    </div>
                  )}
                </div>
              </FadeIn>

              {/* Shipping Address */}
              <FadeIn delay={0.1}>
                <div className="bg-white p-6 border border-[var(--champagne)]">
                  <h3 className="font-['Cormorant_Garamond'] text-xl mb-4 text-[var(--charcoal)]">
                    Adresse de livraison
                  </h3>
                  {order.shipping_address_details ? (
                    <div className="text-[var(--charcoal)]/70">
                      <p className="font-medium text-[var(--charcoal)]">{order.shipping_address_details.street}</p>
                      {order.shipping_address_details.complement && (
                        <p>{order.shipping_address_details.complement}</p>
                      )}
                      <p>
                        {order.shipping_address_details.postal_code} {order.shipping_address_details.city}
                      </p>
                      <p>{order.shipping_address_details.country}</p>
                    </div>
                  ) : order.shipping_address ? (
                    <p className="text-[var(--charcoal)]/70">{order.shipping_address}</p>
                  ) : (
                    <p className="text-[var(--charcoal)]/50">Non renseignée</p>
                  )}
                </div>
              </FadeIn>

              {/* Order Items */}
              {order.items && order.items.length > 0 && (
                <FadeIn delay={0.2}>
                  <div className="bg-white p-6 border border-[var(--champagne)]">
                    <h3 className="font-['Cormorant_Garamond'] text-xl mb-4 text-[var(--charcoal)]">
                      Contenu de la commande ({order.items.length} article{order.items.length > 1 ? 's' : ''})
                    </h3>
                    <div className="space-y-4">
                      {order.items.map((item, idx) => {
                        const firstPhotoUrl = getDisplayablePhotoUrl(item.photos?.[0]);
                        const isFormationProduct = item.type === 'formation_product';
                        return (
                        <div key={idx} className="flex gap-4 p-4 bg-[var(--champagne)]/20">
                          {/* Preview */}
                          <div className="w-20 h-20 shrink-0 bg-white border border-[var(--champagne)] flex items-center justify-center">
                            {firstPhotoUrl ? (
                              <img
                                src={firstPhotoUrl}
                                alt="Preview"
                                className="w-full h-full object-cover"
                              />
                            ) : (
                              <Package size={28} className="text-[var(--gold)]" />
                            )}
                          </div>
                          
                          {/* Details */}
                          <div className="flex-1">
                            <p className="font-medium text-[var(--charcoal)]">
                              {isFormationProduct ? item.product_name || 'Produit formation' : 'Press On Nails'}
                            </p>
                            {isFormationProduct ? (
                              <p className="text-sm text-[var(--charcoal)]/70 mt-1">Produit physique</p>
                            ) : (
                              <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm text-[var(--charcoal)]/70 mt-1">
                                <div>
                                  <span className="text-[var(--charcoal)]/50">Forme:</span>{' '}
                                  {item.shapeLabel || shapeLabels[item.shape] || item.shape}
                                </div>
                                <div>
                                  <span className="text-[var(--charcoal)]/50">Longueur:</span>{' '}
                                  {item.lengthLabel || lengthLabels[item.length] || item.length}
                                </div>
                                <div className="flex items-center gap-2">
                                  <span className="text-[var(--charcoal)]/50">Couleur:</span>
                                  <div
                                    className="w-4 h-4 rounded-full border border-gray-200"
                                    style={{ backgroundColor: item.color }}
                                  />
                                  {item.colorLabel && <span>{item.colorLabel}</span>}
                                </div>
                                {item.nailArt && item.nailArt !== 'none' && (
                                  <div>
                                    <span className="text-[var(--charcoal)]/50">Nail Art:</span>{' '}
                                    {item.nailArtLabel || item.nailArt}
                                  </div>
                                )}
                              </div>
                            )}
                            {!isFormationProduct && item.nailArtDescription && (
                              <p className="mt-2 text-xs text-[var(--charcoal)]/50 italic">
                                "{item.nailArtDescription}"
                              </p>
                            )}
                            
                            {/* Photos section */}
                            {!isFormationProduct && item.photos && item.photos.length > 0 && (
                              <div className="mt-3 pt-3 border-t border-[var(--champagne)]/50">
                                <p className="text-xs text-[var(--charcoal)]/50 mb-2">Photos de référence :</p>
                                <div className="flex gap-2 flex-wrap">
                                  {item.photos.map((photo, photoIdx) => {
                                    const photoUrl = getDisplayablePhotoUrl(photo);
                                    if (!photoUrl) {
                                      return (
                                        <div
                                          key={photoIdx}
                                          className="w-16 h-16 border border-[var(--champagne)] bg-white text-[10px] text-[var(--charcoal)]/50 flex items-center justify-center text-center px-1"
                                        >
                                          Indispo
                                        </div>
                                      );
                                    }
                                    return (
                                      <a
                                        key={photoIdx}
                                        href={photoUrl}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="block relative group"
                                      >
                                        <img
                                          src={photoUrl}
                                          alt={photo.type === 'left_hand' ? 'Main gauche' : photo.type === 'right_hand' ? 'Main droite' : photo.type === 'inspiration' ? 'Inspiration' : `Photo ${photoIdx + 1}`}
                                          className="w-16 h-16 object-cover border border-[var(--champagne)] hover:opacity-80 transition-opacity"
                                        />
                                        <span className="absolute -bottom-4 left-0 right-0 text-center text-[10px] text-[var(--charcoal)]/60">
                                          {photo.type === 'left_hand' && 'Gauche'}
                                          {photo.type === 'right_hand' && 'Droite'}
                                          {photo.type === 'inspiration' && 'Inspi.'}
                                          {!photo.type && `#${photoIdx + 1}`}
                                        </span>
                                      </a>
                                    );
                                  })}
                                </div>
                              </div>
                            )}
                          </div>
                          
                          {/* Price */}
                          <div className="text-right">
                            <p className="font-['Cormorant_Garamond'] text-xl text-[var(--gold)]">
                              {item.price?.toFixed(2)}€
                            </p>
                          </div>
                        </div>
                      )})}
                    </div>
                  </div>
                </FadeIn>
              )}

              {/* Gift Card Details */}
              {order.gift_card_details && order.gift_card_details.length > 0 && (
                <FadeIn delay={0.25}>
                  <div className="bg-white p-6 border border-[var(--champagne)]">
                    <h3 className="font-['Cormorant_Garamond'] text-xl mb-4 text-[var(--charcoal)] flex items-center gap-2">
                      <Gift size={20} className="text-[var(--rose-gold)]" />
                      Cartes Cadeaux ({order.gift_card_details.length})
                    </h3>
                    <div className="space-y-4">
                      {order.gift_card_details.map((gc, idx) => (
                        <div key={idx} className="flex gap-4 p-4 bg-gradient-to-r from-[var(--rose-gold)]/10 to-[var(--champagne)]/20 border border-[var(--rose-gold)]/20">
                          {/* Gift card icon */}
                          <div className="w-20 h-20 shrink-0 bg-white border border-[var(--rose-gold)]/30 flex items-center justify-center">
                            <Gift size={32} className="text-[var(--gold)]" />
                          </div>
                          
                          {/* Details */}
                          <div className="flex-1">
                            <p className="font-medium text-[var(--charcoal)]">
                              Carte Cadeau
                              {gc.prestation_name && (
                                <span className="font-normal text-[var(--charcoal)]/60"> — {gc.prestation_name}</span>
                              )}
                            </p>
                            <div className="space-y-1 text-sm text-[var(--charcoal)]/70 mt-2">
                              <div>
                                <span className="text-[var(--charcoal)]/50">Type:</span>{' '}
                                {gc.gift_type === 'preset_amount' && 'Montant prédéfini'}
                                {gc.gift_type === 'custom_amount' && 'Montant personnalisé'}
                                {gc.gift_type === 'prestation' && 'Prestation'}
                              </div>
                              {gc.receiver_name && (
                                <div>
                                  <span className="text-[var(--charcoal)]/50">Pour:</span> {gc.receiver_name}
                                </div>
                              )}
                              {gc.sender_name && (
                                <div>
                                  <span className="text-[var(--charcoal)]/50">De:</span> {gc.sender_name}
                                </div>
                              )}
                              {gc.code && (
                                <div className="mt-2">
                                  <span className="text-[var(--charcoal)]/50">Code:</span>{' '}
                                  <span className="font-mono bg-[var(--champagne)] px-2 py-1 text-[var(--gold)]">
                                    {gc.code}
                                  </span>
                                </div>
                              )}
                            </div>
                            {gc.personal_message && (
                              <p className="mt-2 text-xs text-[var(--charcoal)]/50 italic">
                                "{gc.personal_message}"
                              </p>
                            )}
                          </div>
                          
                          {/* Price */}
                          <div className="text-right">
                            <p className="font-['Cormorant_Garamond'] text-xl text-[var(--gold)]">
                              {gc.amount?.toFixed(2)}€
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </FadeIn>
              )}

              {/* Legacy Press On Details */}
              {order.press_on_details && !order.items && (
                <FadeIn delay={0.2}>
                  <div className="bg-white p-6 border border-[var(--champagne)]">
                    <h3 className="font-['Cormorant_Garamond'] text-xl mb-4 text-[var(--charcoal)]">
                      Détails Press On Nails
                    </h3>
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <span className="text-[var(--charcoal)]/60">Forme</span>
                        <p className="font-medium">{shapeLabels[order.press_on_details.shape]}</p>
                      </div>
                      <div>
                        <span className="text-[var(--charcoal)]/60">Longueur</span>
                        <p className="font-medium">{lengthLabels[order.press_on_details.length]}</p>
                      </div>
                      <div>
                        <span className="text-[var(--charcoal)]/60">Couleur</span>
                        <div className="flex items-center gap-2">
                          <div
                            className="w-6 h-6 border border-gray-300"
                            style={{ backgroundColor: order.press_on_details.color }}
                          />
                          <span className="font-mono text-xs">{order.press_on_details.color}</span>
                        </div>
                      </div>
                      <div>
                        <span className="text-[var(--charcoal)]/60">Nail Art</span>
                        <p className="font-medium">{order.press_on_details.has_nail_art ? 'Oui' : 'Non'}</p>
                      </div>
                    </div>
                    {order.press_on_details.nail_art_description && (
                      <div className="mt-4 pt-4 border-t border-[var(--champagne)]">
                        <span className="text-[var(--charcoal)]/60 text-sm">Description Nail Art</span>
                        <p className="mt-1">{order.press_on_details.nail_art_description}</p>
                      </div>
                    )}
                    {order.press_on_details.photos?.length > 0 && (
                      <div className="mt-4 pt-4 border-t border-[var(--champagne)]">
                        <span className="text-[var(--charcoal)]/60 text-sm">Photos de référence</span>
                        <div className="grid grid-cols-2 gap-4 mt-2">
                          {order.press_on_details.photos.map((photo, i) => {
                            const photoUrl = getDisplayablePhotoUrl(photo);
                            if (!photoUrl) {
                              return (
                                <div
                                  key={i}
                                  className="w-full h-40 border border-[var(--champagne)] bg-white text-xs text-[var(--charcoal)]/50 flex items-center justify-center"
                                >
                                  Image indisponible
                                </div>
                              );
                            }
                            return (
                              <a
                                key={i}
                                href={photoUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="block"
                              >
                                <img
                                  src={photoUrl}
                                  alt={`Reference ${i + 1}`}
                                  className="w-full h-40 object-cover border border-[var(--champagne)] hover:opacity-80 transition-opacity"
                                />
                              </a>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                </FadeIn>
              )}

              {/* Client Notes */}
              {order.notes && (
                <FadeIn delay={0.3}>
                  <div className="bg-white p-6 border border-[var(--champagne)]">
                    <h3 className="font-['Cormorant_Garamond'] text-xl mb-4 text-[var(--charcoal)]">
                      Notes du client
                    </h3>
                    <p className="text-[var(--charcoal)]/70">{order.notes}</p>
                  </div>
                </FadeIn>
              )}
            </div>

            {/* Sidebar - Status, Price & Actions */}
            <div className="space-y-6">
              {/* Price Summary */}
              <FadeIn>
                <div className="bg-white p-6 border border-[var(--champagne)]">
                  <h3 className="font-['Cormorant_Garamond'] text-xl mb-4 text-[var(--charcoal)]">
                    Récapitulatif
                  </h3>
                  <div className="space-y-3 text-sm">
                    <div className="flex justify-between">
                      <span className="text-[var(--charcoal)]/60">Sous-total</span>
                      <span>{order.subtotal?.toFixed(2) || '—'}€</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-[var(--charcoal)]/60">Livraison</span>
                      <span className={order.delivery_fee === 0 ? 'text-green-600 font-medium' : ''}>
                        {order.delivery_fee === 0 ? 'Offerte' : order.delivery_fee !== null ? `${order.delivery_fee?.toFixed(2)}€` : '—'}
                      </span>
                    </div>
                    <div className="border-t border-[var(--champagne)] pt-3 flex justify-between items-center">
                      <span className="font-medium text-[var(--charcoal)]">Total</span>
                      <span className="font-['Cormorant_Garamond'] text-2xl text-[var(--gold)]">
                        {order.total_price?.toFixed(2) || '—'}€
                      </span>
                    </div>
                  </div>
                </div>
              </FadeIn>

              {/* Status */}
              <FadeIn delay={0.1}>
                <div className="bg-white p-6 border border-[var(--champagne)]">
                  <h3 className="font-['Cormorant_Garamond'] text-xl mb-4 text-[var(--charcoal)]">
                    Statut
                  </h3>
                  <div className={`inline-block px-3 py-1.5 text-sm rounded mb-4 ${getStatusColor(order.status)}`}>
                    {allStatusOptions.find(s => s.value === order.status)?.label || order.status}
                  </div>

                  <Select
                    value={statusOptions.some((s) => s.value === order.status) ? order.status : undefined}
                    onValueChange={updateStatus}
                    disabled={isUpdating}
                  >
                    <SelectTrigger className="rounded-none" data-testid="select-status">
                      <SelectValue placeholder="Statut historique non modifiable" />
                    </SelectTrigger>
                    <SelectContent>
                      {statusOptions.map(s => (
                        <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </FadeIn>

              <FadeIn delay={0.2}>
                <div className="bg-white p-6 border border-[var(--champagne)]">
                  <h3 className="font-['Cormorant_Garamond'] text-xl mb-4 text-[var(--charcoal)]">
                    Notes internes
                  </h3>
                  <Textarea
                    value={adminNotes}
                    onChange={(e) => setAdminNotes(e.target.value)}
                    placeholder="Notes visibles uniquement par l'admin..."
                    className="min-h-[100px] rounded-none mb-4"
                    data-testid="admin-notes"
                  />
                  <Button
                    onClick={() => updateStatus(order.status)}
                    disabled={isUpdating}
                    className="w-full bg-[var(--charcoal)] text-white hover:bg-[var(--charcoal)]/80 rounded-none"
                  >
                    {isUpdating ? <Loader2 size={16} className="animate-spin mr-2" /> : null}
                    Enregistrer les notes
                  </Button>
                </div>
              </FadeIn>

              <FadeIn delay={0.3}>
                <div className="bg-[var(--champagne)]/30 p-6 border border-[var(--champagne)]">
                  <h4 className="text-sm uppercase tracking-wider text-[var(--charcoal)]/60 mb-3">
                    Historique
                  </h4>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-[var(--charcoal)]/60">Créée</span>
                      <span>{formatDate(order.created_at)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-[var(--charcoal)]/60">Modifiée</span>
                      <span>{formatDate(order.updated_at)}</span>
                    </div>
                    {order.payment_method && (
                      <div className="flex justify-between">
                        <span className="text-[var(--charcoal)]/60">Paiement</span>
                        <span>{order.payment_method === 'mock' ? 'Mode test' : order.payment_method}</span>
                      </div>
                    )}
                  </div>
                </div>
              </FadeIn>
            </div>
          </div>
        </div>
      </section>
    </PageTransition>
  );
}
